import { useMemo, useCallback, useState, useEffect } from 'react'
import useCartStore from '../stores/cartStore'

export default function SeatMap({
    hall,
    seats,
    soldSeatIds = [],
    lockedSeatIds = [],
    onSeatClick,
    selectedSector
}) {
    const { selectedSeats, selectSeat } = useCartStore()

    const selectedSeatIds = useMemo(
        () => new Set(selectedSeats.map(s => s.id)),
        [selectedSeats]
    )

    const getSeatStatus = useCallback((seat) => {
        if (soldSeatIds.includes(seat.id)) return 'sold'
        if (seat.type === 'reserved') return 'reserved'
        if (lockedSeatIds.includes(seat.id)) return 'locked'
        if (selectedSeatIds.has(seat.id)) return 'selected'
        return 'available'
    }, [soldSeatIds, lockedSeatIds, selectedSeatIds])

    const handleSeatClick = (seat) => {
        const status = getSeatStatus(seat)
        if (status === 'sold' || status === 'locked' || status === 'reserved') return

        selectSeat(seat)
        onSeatClick?.(seat)
    }

    // Group seats by row
    const seatsByRow = useMemo(() => {
        const grouped = {}
        seats.forEach(seat => {
            const row = seat.row || 'GA'
            if (!grouped[row]) grouped[row] = []
            grouped[row].push(seat)
        })
        // Sort seats within each row by number
        Object.keys(grouped).forEach(row => {
            grouped[row].sort((a, b) => parseInt(a.number) - parseInt(b.number))
        })
        return grouped
    }, [seats])

    // const rows = Object.keys(seatsByRow).sort((x, y) => x - y)

    const rows = Object.keys(seatsByRow)
        .map(key => seatsByRow[key][0])
        .sort((x, y) => x.pos_y - y.pos_y)
        .map(x => x.row);

    ////////////////////////////////////////////////////////////////////
    let isSymmetrical = selectedSector?.is_symmetrical;

    const [rowSize, setRowSize] = useState({ width: 0, max: 0, min: 0 })
    const seatWidth = 36;
    // const seatGap = 8; //--> gap-2 is 8px

    useEffect(() => {
        let allMin = [];
        let allMax = [];
        let maxSeatsInRow = 0;

        rows.map(row => {
            const seatsInRow = seatsByRow[row];
            const minPosX = Math.min(...seatsInRow.map(seat => seat.pos_x));
            const maxPosX = Math.max(...seatsInRow.map(seat => seat.pos_x), 0);

            allMin.push(minPosX)
            allMax.push(maxPosX)

            const allSeatsInRow = seatsInRow.length

            if (maxSeatsInRow < allSeatsInRow) {
                maxSeatsInRow = allSeatsInRow
            }
        })

        const min = Math.min(...allMin);
        const max = Math.max(...allMax);

        const width = max - min + seatWidth;

        setRowSize({ width, max, min });
    }, [seats])


    return (
        <div className="w-full m-auto" style={{ width: 'calc(100% - 60px)' }}>

            {rows?.length > 0 &&
                <>
                    {/* Screen/Stage indicator */}
                    <div className="mb-8">
                        <div className="max-w-md mx-auto">
                            <div className="h-2 bg-gradient-to-r from-transparent via-primary-500 to-transparent rounded-full" />
                            <p className="text-center text-gray-500 text-sm mt-2">
                                {window.t('scena_ekran')}
                            </p>
                        </div>
                    </div>

                    {/* Seat Grid */}
                    <div className="w-full overflow-x-auto pb-4">
                        <div
                            className="min-w-full w-max m-auto"
                            style={{ width: `${rowSize.width}px` }}
                        >
                            {rows.map(row => {
                                const seatsInRow = seatsByRow[row];

                                const maxPosX = Math.max(...seatsInRow.map(seat => seat.pos_x), 0);
                                const minPosX = Math.min(...seatsInRow.map(seat => seat.pos_x));

                                return (
                                    <div key={row} className="relative flex items-center justify-center min-w-full mb-2">
                                        <span className="flex-none fixed left-[15px] z-[1] pt-[4px] w-8 text-center text-gray-500 text-sm font-medium">
                                            {row}
                                        </span>

                                        <div className="h-[28px]">
                                            <div
                                                className="relative h-full"
                                                style={
                                                    isSymmetrical
                                                        ? { width: `${maxPosX - minPosX + seatWidth}px` }
                                                        : { width: `${rowSize.width + rowSize.min}px`, marginLeft: `${-rowSize.min}px` }
                                                }
                                            >
                                                {seatsInRow.map(seat => {
                                                    const status = getSeatStatus(seat);

                                                    return (
                                                        <button
                                                            key={seat.id}
                                                            onClick={() => handleSeatClick(seat)}
                                                            disabled={status === 'sold' || status === 'locked' || status === 'reserved'}
                                                            className={`
                                                                absolute
                                                                seat
                                                                ${status === 'available' && 'seat-available'}
                                                                ${status === 'selected' && 'seat-selected'}
                                                                ${status === 'sold' && 'seat-sold'}
                                                                ${status === 'locked' && 'seat-locked'}
                                                                ${status === 'reserved' && 'seat-reserved'}
                                                                ${seat.type === 'vip' && 'seat-vip'}
                                                                ${seat.type === 'accessible' && 'seat-accessible'}
                                                              `}
                                                            title={`${row}${seat.number} - ${seat.type}`}
                                                            style={
                                                                isSymmetrical
                                                                    ? { left: `${seat.pos_x - minPosX}px` }
                                                                    : { left: `${seat.pos_x}px` }
                                                            }
                                                        >
                                                            {seat.number}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>


                                        <span className="flex-none fixed right-[15px] pt-[4px] w-8 text-center text-gray-500 text-sm font-medium">
                                            {row}
                                        </span>

                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            }

            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-6 mt-8 pt-6 border-t border-gray-800">
                <div className="flex items-center gap-2">
                    <div className="seat seat-available w-6 h-6 text-xs"></div>
                    <span className="text-gray-400 text-sm">{window.t('svobodno')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="seat seat-selected w-6 h-6 text-xs"></div>
                    <span className="text-gray-400 text-sm">{window.t('izbrano')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="seat seat-sold w-6 h-6 text-xs"></div>
                    <span className="text-gray-400 text-sm">{window.t('prodadeno')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="seat seat-reserved w-6 h-6 text-xs"></div>
                    <span className="text-gray-400 text-sm">{window.t('rezervirano')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="seat seat-available seat-vip w-6 h-6 text-xs"></div>
                    <span className="text-gray-400 text-sm">VIP</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="seat seat-available seat-accessible w-6 h-6 text-xs"></div>
                    <span className="text-gray-400 text-sm">{window.t('za_hora_s_uvrezhdaniya')}</span>
                </div>
            </div>
        </div>
    )
}
